/**
 * ProseText — substitui o despejo cru de texto ({whiteSpace:'pre-wrap'}) dentro
 * dos cards premium por parágrafos curtos / listas quando o texto é excessivo.
 *
 * Não introduz cores, bordas, ícones ou espaçamentos novos: reaproveita o
 * `style` recebido (o mesmo que já era usado no <p>/<div> original). Texto
 * curto renderiza exatamente como antes — zero diff visual.
 */
import React from 'react';
import { isExcessiveProse, splitProseIntoBlocks, tokenizeInlineBold } from '../../utils/proseFormatter';

const CARD_THRESHOLD = 220;

// Preserva negrito moderado (**texto**) que já existia nos componentes antes
// desta mudança — não é uma feature nova, é a mesma marcação sendo respeitada.
// `boldStyle` deixa o chamador manter a cor/peso de negrito que já usava.
function InlineText({ text, boldStyle }) {
  const tokens = tokenizeInlineBold(text);
  return tokens.map((t, i) => t.bold
    ? <strong key={i} style={boldStyle || { fontWeight: 600 }}>{t.text}</strong>
    : <React.Fragment key={i}>{t.text}</React.Fragment>);
}

export function ProseText({ text, style, as = 'p', cardMode = true, boldStyle }) {
  if (!text) return null;

  const opts = cardMode ? { threshold: CARD_THRESHOLD } : undefined;

  if (!isExcessiveProse(text, opts)) {
    const Tag = as;
    return <Tag style={style}><InlineText text={text} boldStyle={boldStyle} /></Tag>;
  }

  const blocks = splitProseIntoBlocks(text, opts);
  const { margin, ...restStyle } = style || {};

  return (
    <>
      {blocks.map((b, i) => {
        const isLast = i === blocks.length - 1;
        const blockMargin = isLast ? 0 : '0 0 8px';

        if (b.type === 'list') {
          return (
            <ul key={i} style={{ ...restStyle, margin: blockMargin, paddingLeft: 18, listStyle: 'disc' }}>
              {b.items.map((item, j) => (
                <li key={j} style={{ marginBottom: 3 }}><InlineText text={item} boldStyle={boldStyle} /></li>
              ))}
            </ul>
          );
        }
        if (b.type === 'heading') {
          return (
            <div key={i} style={{ ...restStyle, margin: blockMargin, fontWeight: 700, opacity: 0.92 }}>
              {b.text}
            </div>
          );
        }
        // paragraph | raw | code — mesmo estilo do texto original, só quebrado em blocos
        return (
          <p key={i} style={{ ...restStyle, margin: blockMargin, whiteSpace: 'pre-wrap' }}>
            <InlineText text={b.text} boldStyle={boldStyle} />
          </p>
        );
      })}
    </>
  );
}

export default ProseText;
